import { useCallback } from "react"

import { RUNS_FILTER_STORAGE_KEY } from "@/constants/storage-keys"
import type { OrganizationResource } from "@clerk/shared/types"
import { useQueryClient } from "@tanstack/react-query"

import { logger } from "@/lib/logger"
import { buildOrganizationHomePath } from "@/utils/workspaceRoutes"

const log = logger("SyncActiveOrganization")

const getOrganizationHomePath = (organizationSlug?: string | null): string =>
  organizationSlug ? buildOrganizationHomePath(organizationSlug) : "/home"

interface UseSyncActiveOrganizationReturn {
  handleOrgSwitch: (organization: OrganizationResource) => string
}

/** Supplies Clerk's switcher with the destination for an explicit selection. */
export const useSyncActiveOrganization =
  (): UseSyncActiveOrganizationReturn => {
    const queryClient = useQueryClient()

    const handleOrgSwitch = useCallback(
      (organization: OrganizationResource): string => {
        queryClient.clear()

        if (typeof window !== "undefined") {
          try {
            window.localStorage.removeItem(RUNS_FILTER_STORAGE_KEY)

            const keysToRemove: string[] = []
            for (let i = 0; i < window.localStorage.length; i++) {
              const key = window.localStorage.key(i)
              if (key?.startsWith("run-filter-")) {
                keysToRemove.push(key)
              }
            }
            keysToRemove.forEach((key) => window.localStorage.removeItem(key))
          } catch (error) {
            log.warn("Error clearing filters on organization switch", { error })
          }
        }

        return getOrganizationHomePath(organization.slug)
      },
      [queryClient],
    )

    return { handleOrgSwitch }
  }
